import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Property photo for a drawer header. Falls back to the given icon when there
 * is no image, and again if the image fails to load.
 */
export function PropertyThumbnail({ imageUrl, alt, fallback }: { imageUrl?: string; alt: string; fallback: ReactNode }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  return (
    <div className="grid h-[70px] w-[70px] flex-shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-muted/40 text-muted-foreground">
      {imageUrl && failedUrl !== imageUrl ? (
        <img src={imageUrl} alt={alt} className="h-full w-full object-cover" onError={() => setFailedUrl(imageUrl)} />
      ) : (
        <span aria-hidden>{fallback}</span>
      )}
    </div>
  );
}

/** Labeled panel that groups related fields inside a detail drawer. */
export function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-muted/40 p-4">
      <div className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <span className="text-primary" aria-hidden>{icon}</span> {title}
      </div>
      {children}
    </section>
  );
}

/** Read-only figure shown alongside a drawer's editable fields. */
export function StatTile({ label, value, valueClassName }: { label: string; value: string; valueClassName?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-1 text-base font-bold tabular-nums text-foreground", valueClassName)}>{value}</div>
    </div>
  );
}
